'use client'

import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import Image from 'next/image'
import { useRouter, usePathname } from 'next/navigation'
import { ArrowRight, Check, CheckCircle2, ChevronRight, Clock, Copy, ExternalLink, Gift, Loader2, MessageCircle, Printer, Send, Sparkles, XCircle, Zap } from 'lucide-react'
import { toast } from '@/lib/toast'
import QRCodeImport from 'react-qr-code'
import qpayLogo from '@/assets/qpay.svg'
import { formatDateTimeLong, formatDateTimeNormal, formatImageURL, formatPrice } from '@/utils/util'
import { STATUS_LABELS } from '@/utils/gift'
import { navigateUrl } from '@/utils/client-request'
import { useGiftCardPaymentChecker, useInAppBrowser, IN_APP_BROWSER_LABELS } from '@/lib/commerce/hooks'

const QRCode = QRCodeImport as unknown as React.FC<{ value: string, size?: number, fgColor?: string, bgColor?: string, style?: React.CSSProperties }>

type StatusToneKey = 'amber' | 'blue' | 'emerald' | 'rose' | 'muted'
const TONES: Record<StatusToneKey, { bg: string, color: string, border: string }> = {
  amber: { bg: 'rgba(245,158,11,0.14)', color: '#fbbf24', border: 'rgba(245,158,11,0.3)' },
  blue: { bg: 'rgba(59,130,246,0.14)', color: '#93c5fd', border: 'rgba(59,130,246,0.3)' },
  emerald: { bg: 'rgba(16,185,129,0.14)', color: '#34d399', border: 'rgba(16,185,129,0.3)' },
  rose: { bg: 'rgba(244,63,94,0.14)', color: '#fda4af', border: 'rgba(244,63,94,0.3)' },
  muted: { bg: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.7)', border: 'rgba(255,255,255,0.13)' },
}
const STATUS_TONE_MAP: Record<string, StatusToneKey> = {
  pending: 'amber',
  paid: 'emerald',
  sent: 'blue',
  used: 'emerald',
  canceled: 'rose',
}

function Section({ eyebrow, children }: { eyebrow: React.ReactNode, children: React.ReactNode }) {
  return (
    <div className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-4 inline-flex items-center gap-2" style={{ color: '#a8a8b3' }}>
        {eyebrow}
      </div>
      {children}
    </div>
  )
}

function InfoRow({ icon: Icon, label, children }: { icon: any, label: string, children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 py-3" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
      <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: 'rgba(255,255,255,0.5)' }} />
      <div className="flex-1 min-w-0 grid sm:grid-cols-[140px_1fr] gap-0.5 sm:gap-3 items-baseline">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold" style={{ color: '#a8a8b3' }}>{label}</div>
        <div className="text-[13px] text-white break-words">{children}</div>
      </div>
    </div>
  )
}

function QRFrame({ value, accent, logoSrc, size = 200 }: { value: string, accent: string, logoSrc?: any, size?: number }) {
  if (!value) {
    return <div className="rounded-lg animate-pulse" style={{ width: size + 32, height: size + 32, background: 'rgba(255,255,255,0.04)' }} />
  }
  const corners = [
    { pos: { top: 0, left: 0 }, border: 'border-t-2 border-l-2 rounded-tl-xl' },
    { pos: { top: 0, right: 0 }, border: 'border-t-2 border-r-2 rounded-tr-xl' },
    { pos: { bottom: 0, left: 0 }, border: 'border-b-2 border-l-2 rounded-bl-xl' },
    { pos: { bottom: 0, right: 0 }, border: 'border-b-2 border-r-2 rounded-br-xl' },
  ]
  return (
    <div className="relative inline-block" style={{ width: size + 32, height: size + 32 }}>
      <div className="absolute inset-0 pointer-events-none">
        {corners.map((c, i) => (
          <div key={i} className={'absolute w-5 h-5 ' + c.border} style={{ ...c.pos, borderColor: accent }} />
        ))}
      </div>
      <div className="absolute inset-2 rounded-lg bg-white p-3">
        <QRCode value={value} size={size - 8} fgColor="#0a0a0c" bgColor="#ffffff" style={{ width: '100%', height: '100%' }} />
        {logoSrc && (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-md bg-white border border-zinc-200 shadow grid place-items-center p-1">
            <Image src={logoSrc} alt="" className="max-w-full max-h-full object-contain" />
          </div>
        )}
      </div>
    </div>
  )
}

function NumberedSteps({ steps, accent }: { steps: string[], accent: string }) {
  return (
    <ol className="space-y-2 text-[13px]" style={{ color: 'rgba(255,255,255,0.85)' }}>
      {steps.map((s, i) => (
        <li key={i} className="flex gap-2.5">
          <span className="shrink-0 w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold mt-0.5 text-white" style={{ background: accent }}>
            {i + 1}
          </span>
          <span>{s}</span>
        </li>
      ))}
    </ol>
  )
}

function PaymentSuccessCard() {
  return (
    <div className="rounded-2xl p-5 flex items-center gap-3" style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.3)' }}>
      <div className="w-11 h-11 rounded-full grid place-items-center shrink-0" style={{ background: 'rgba(16,185,129,0.18)', color: '#34d399', border: '1px solid rgba(16,185,129,0.3)' }}>
        <CheckCircle2 className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-semibold text-white">Төлбөр амжилттай төлөгдлөө</div>
        <div className="text-[12px] mt-0.5" style={{ color: 'rgba(255,255,255,0.6)' }}>Бэлгийн карт баталгаажиж, илгээх өдөр хүлээн авагчид имэйлээр хүргэгдэнэ.</div>
      </div>
    </div>
  )
}

function PendingPayment({ giftCard, onSuccess }: { giftCard: any, onSuccess: () => void }) {
  const { loading, success, check } = useGiftCardPaymentChecker({ giftCard, onSuccess })
  const { isInAppBrowser, appLinksBlocked, browserUrl, openInBrowser } = useInAppBrowser({ giftCard })
  if (success) return <PaymentSuccessCard />
  if (!giftCard?.qpay) return null
  const banks = giftCard?.qpay?.urls || []

  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
      <div className="px-5 pt-5 pb-4 flex items-center gap-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="w-11 h-11 rounded-lg grid place-items-center shrink-0 p-2 overflow-hidden" style={{ background: 'white', border: '1px solid rgba(255,255,255,0.13)' }}>
          <Image src={qpayLogo} alt="QPay" className="w-full h-full object-contain" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: '#a8a8b3' }}>Төлбөрийн арга</div>
          <div className="text-[15px] font-semibold text-white truncate" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>
            QPay<span className="font-normal" style={{ color: 'rgba(255,255,255,0.55)' }}> · QR төлбөр</span>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-1.5 text-[11px] font-medium" style={{ color: '#34d399' }}>
          <Zap className="h-3.5 w-3.5" />Шуурхай
        </div>
      </div>
      <div className="grid sm:grid-cols-[auto_1fr] gap-5 p-5">
        <div className="flex flex-col items-center">
          <QRFrame value={giftCard?.qpay?.QRCode} accent="#ffffff" logoSrc={qpayLogo} size={200} />
          <div className="mt-2 text-[11px] font-semibold text-center" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Карт <span className="font-mono text-white">#{giftCard?.customId}</span>
          </div>
        </div>
        <div className="flex flex-col">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: '#a8a8b3' }}>Төлөх дүн</div>
          <div className="text-[28px] font-bold tabular-nums tracking-tight text-white mt-1" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>
            {formatPrice(giftCard?.amount)}
          </div>
          <div className="mt-4">
            <NumberedSteps
              accent="rgba(255,255,255,0.14)"
              steps={[
                'Банкны аппаа нээгээд QR уншуулах цэс рүү орно уу',
                'Дээрх QR кодыг уншуулан төлбөрөө баталгаажуулна уу',
              ]}
            />
          </div>
          <div className="mt-5">
            <button
              onClick={check}
              disabled={loading}
              className="w-full h-11 rounded-full text-[13px] font-semibold inline-flex items-center justify-center gap-2 transition-opacity disabled:opacity-60 cursor-pointer"
              style={{ background: 'white', color: '#0a0a0c' }}
            >
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Шалгаж байна…</> : <>Төлбөр шалгах <ArrowRight className="h-3.5 w-3.5" /></>}
            </button>
          </div>
        </div>
      </div>
      {banks.length > 0 && (
        <div className="px-5 pb-5 sm:hidden">
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-2" style={{ color: '#a8a8b3' }}>Банкны апп шууд нээх</div>
          {isInAppBrowser && (
            <div className="rounded-xl px-4 py-3.5 mb-3" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.13)' }}>
              <p className="text-[12px]" style={{ color: 'rgba(255,255,255,0.75)' }}>{IN_APP_BROWSER_LABELS.message}</p>
              <a href={browserUrl} onClick={openInBrowser} className="mt-3 inline-flex items-center gap-2 justify-center text-[13px] font-semibold rounded-full py-2.5 px-4" style={{ background: 'white', color: '#0a0a0c' }}>
                {IN_APP_BROWSER_LABELS.action}<ExternalLink className="w-3.5 h-3.5" />
              </a>
              <p className="mt-2.5 text-[11px]" style={{ color: 'rgba(255,255,255,0.55)' }}>{IN_APP_BROWSER_LABELS.hint}</p>
            </div>
          )}
          {!appLinksBlocked && (
            <div className="grid grid-cols-6 gap-2">
              {banks.map((b: any, i: number) => (
                <a key={i} href={b.link} className="aspect-square rounded-lg overflow-hidden" style={{ background: 'white', border: '1px solid rgba(255,255,255,0.13)' }}>
                  <img src={b.logo} alt={b.name || `Bank ${i + 1}`} className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function PaidBanner({ giftCard, status }: { giftCard: any, status: string }) {
  const isUsed = status === 'used'
  const isSent = status === 'sent'
  const eyebrow = isUsed ? 'Карт ашиглагдсан' : isSent ? 'Карт илгээгдсэн' : 'Төлбөр амжилттай'
  const title = isUsed ? 'Хүлээн авагч ашигласан' : isSent ? 'Илгээгдлээ' : 'Захиалга баталгаажигдлаа'
  const subtitle = isUsed
    ? 'Бэлгийн карт амжилттай ашиглагдсан байна.'
    : isSent
      ? 'Хүлээн авагчийн имэйл рүү бэлгийн картыг илгээлээ.'
      : 'Илгээх өдөр хүлээн авагчид имэйлээр бэлгийн карт хүргэгдэнэ.'
  return (
    <div
      className="relative overflow-hidden rounded-2xl px-5 sm:px-7 py-6 sm:py-8"
      style={{
        background: 'radial-gradient(800px 400px at 80% 0%, rgba(255,255,255,0.08), transparent 60%), radial-gradient(600px 400px at 0% 100%, rgba(255,255,255,0.04), transparent 60%), #0c0c10',
        border: '1px solid rgba(255,255,255,0.13)',
      }}
    >
      <div className="relative flex items-start gap-4">
        <div className="w-11 h-11 rounded-full grid place-items-center shrink-0" style={{ background: 'rgba(16,185,129,0.18)', color: '#34d399', border: '1px solid rgba(16,185,129,0.3)' }}>
          {isUsed ? <Check className="h-5 w-5" /> : isSent ? <Send className="h-5 w-5" /> : <Sparkles className="h-5 w-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: '#a8a8b3' }}>{eyebrow}</div>
          <div className="text-[20px] sm:text-[24px] font-semibold mt-1 leading-tight text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>{title}</div>
          <div className="text-[13.5px] mt-1.5" style={{ color: 'rgba(255,255,255,0.7)' }}>{subtitle}</div>
        </div>
      </div>
      <div className="relative mt-5 grid sm:grid-cols-3 gap-3">
        {[
          { label: 'Карт', value: `#${giftCard?.customId}`, mono: false },
          { label: 'Үнийн дүн', value: formatPrice(giftCard?.amount), mono: true },
          { label: 'Илгээх өдөр', value: giftCard?.deliveryDate ? formatDateTimeNormal(giftCard.deliveryDate) : '—', mono: false },
        ].map((tile, i) => (
          <div key={i} className="rounded-lg px-3 py-2.5" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: 'rgba(255,255,255,0.55)' }}>{tile.label}</div>
            <div className={'text-[14px] font-semibold mt-0.5 text-white ' + (tile.mono ? 'tabular-nums' : '')}>{tile.value}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

function CanceledBanner({ giftCard }: { giftCard: any }) {
  return (
    <div className="rounded-2xl p-5 flex items-center gap-3" style={{ background: TONES.rose.bg, border: `1px solid ${TONES.rose.border}` }}>
      <div className="w-10 h-10 rounded-full grid place-items-center shrink-0" style={{ background: 'rgba(0,0,0,0.25)', color: TONES.rose.color, border: `1px solid ${TONES.rose.border}` }}>
        <XCircle className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-semibold" style={{ color: TONES.rose.color }}>Цуцлагдсан</div>
        <div className="text-[12px] mt-1" style={{ color: 'rgba(255,255,255,0.6)' }}>
          Бэлгийн картын захиалга цуцлагдсан байна
          {giftCard?.canceledAt ? ` · ${formatDateTimeNormal(giftCard.canceledAt)}` : ''}
        </div>
      </div>
    </div>
  )
}

function GiftCardPreview({ giftCard, vendor }: { giftCard: any, vendor?: any }) {
  const recipient = giftCard?.fullName || `${giftCard?.firstName || ''} ${giftCard?.lastName || ''}`.trim()
  const amount = giftCard?.amount || 0
  const hasThumbnail = !!giftCard?.template?.thumbnail
  const cardBg = hasThumbnail
    ? undefined
    : 'linear-gradient(135deg, #1a1a20 0%, #08080a 100%)'
  return (
    <div
      className="relative aspect-1.586/1 rounded-3xl overflow-hidden"
      style={{
        background: cardBg,
        boxShadow: '0 30px 80px -20px rgba(0,0,0,0.6)',
        border: '1px solid rgba(255,255,255,0.13)',
      }}
    >
      {hasThumbnail && (
        <>
          <img
            src={formatImageURL(giftCard.template.thumbnail)}
            alt={giftCard?.template?.name || ''}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.7) 100%)' }} />
        </>
      )}
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, transparent 40%, rgba(0,0,0,0.15) 100%)' }} />
      <div className="relative h-full p-5 sm:p-7 flex flex-col justify-between text-white">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.24em]" style={{ color: 'rgba(255,255,255,0.7)' }}>Бэлгийн карт</div>
            <div className="mt-1 font-bold tracking-[-0.02em]" style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(18px, 2.5vw, 24px)' }}>
              {vendor?.name || 'Shop.mn'}
            </div>
          </div>
          {giftCard?.template?.name && (
            <div className="text-right shrink-0">
              <div className="text-[9px] uppercase tracking-[0.22em]" style={{ color: 'rgba(255,255,255,0.7)' }}>Загвар</div>
              <div className="mt-0.5 text-[10px] truncate max-w-32" style={{ color: 'rgba(255,255,255,0.55)' }}>{giftCard.template.name}</div>
            </div>
          )}
        </div>
        <div className="font-bold tabular-nums tracking-[-0.025em] leading-none" style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(36px, 6vw, 64px)' }}>
          {formatPrice(amount)}
        </div>
        <div className="flex items-end justify-between text-[11px] gap-3">
          <div className="min-w-0">
            <div className="uppercase tracking-[0.2em]" style={{ color: 'rgba(255,255,255,0.7)' }}>Хүлээн авагч</div>
            <div
              className="mt-1 font-semibold truncate"
              style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 16 }}
            >
              {recipient || 'Хүлээн авагчийн нэр'}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="uppercase tracking-[0.2em]" style={{ color: 'rgba(255,255,255,0.7)' }}>№</div>
            <div className="mt-1 font-mono tabular-nums" style={{ color: 'rgba(255,255,255,0.8)' }}>#{giftCard?.customId}</div>
          </div>
        </div>
      </div>
    </div>
  )
}

function CodeBlock({ code }: { code: string }) {
  const [flashed, setFlashed] = useState(false)
  const handle = () => {
    if (!code) return toast.error('Хуулах код байхгүй байна')
    navigator.clipboard?.writeText(code)
    setFlashed(true)
    setTimeout(() => setFlashed(false), 900)
  }
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-2" style={{ color: '#a8a8b3' }}>Код</div>
      <div
        className="rounded-xl p-3 flex items-center justify-between gap-3"
        style={{
          background: flashed ? 'rgba(52,211,153,0.1)' : 'rgba(0,0,0,0.35)',
          border: `1px solid ${flashed ? 'rgba(52,211,153,0.4)' : 'rgba(255,255,255,0.07)'}`,
        }}
      >
        <span className="font-mono tabular-nums text-[14px] text-white tracking-wider break-all">{code}</span>
        <button
          type="button"
          onClick={handle}
          className="h-8 px-3 rounded-full text-[11px] font-medium inline-flex items-center gap-1.5 shrink-0 cursor-pointer transition-colors hover:bg-white/10"
          style={{
            background: 'rgba(255,255,255,0.06)',
            color: flashed ? '#34d399' : 'white',
            border: '1px solid rgba(255,255,255,0.07)',
          }}
        >
          {flashed ? <><CheckCircle2 className="h-3 w-3" /> Хуулсан</> : <><Copy className="h-3 w-3" /> Хуулах</>}
        </button>
      </div>
    </div>
  )
}

function GiftCardSection({ giftCard, vendor }: { giftCard: any, vendor?: any }) {
  const hasCode = !!giftCard?.code
  return (
    <div className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-5 inline-flex items-center gap-2" style={{ color: '#a8a8b3' }}>
        <Gift className="h-3 w-3" /> Бэлгийн карт
      </div>
      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-6 items-start">
        <GiftCardPreview giftCard={giftCard} vendor={vendor} />
        <div className="flex flex-col gap-4">
          {hasCode ? (
            <CodeBlock code={giftCard.code} />
          ) : (
            <div className="rounded-xl p-3" style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-1.5" style={{ color: '#a8a8b3' }}>Код</div>
              <div className="text-[12px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
                Төлбөр баталгаажсаны дараа код илгээгдэнэ.
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg p-3" style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: '#a8a8b3' }}>Картын дүн</div>
              <div className="mt-1 text-[16px] font-semibold tabular-nums text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>
                {formatPrice(giftCard?.amount)}
              </div>
            </div>
            <div className="rounded-lg p-3" style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: '#a8a8b3' }}>Загвар</div>
              <div className="mt-1 text-[14px] font-medium text-white truncate" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>
                {giftCard?.template?.name || '—'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function GiftRecipientCard({ giftCard }: { giftCard: any }) {
  const recipient = giftCard?.fullName || `${giftCard?.firstName || ''} ${giftCard?.lastName || ''}`.trim()
  return (
    <div className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-5" style={{ color: '#a8a8b3' }}>
        Хүлээн авагч
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] mb-1.5" style={{ color: '#a8a8b3' }}>Нэр</div>
          <div className="text-[14px] text-white">{recipient || '—'}</div>
        </div>
        {giftCard?.email && (
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] mb-1.5" style={{ color: '#a8a8b3' }}>И-мэйл</div>
            <div className="text-[14px] text-white break-all">{giftCard.email}</div>
          </div>
        )}
        {giftCard?.deliveryDate && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] mb-1.5" style={{ color: '#a8a8b3' }}>Илгээгдэх</div>
            <div className="text-[13px] text-white tabular-nums">{formatDateTimeNormal(giftCard.deliveryDate)}</div>
          </div>
        )}
        {giftCard?.senderEmail && (
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] mb-1.5" style={{ color: '#a8a8b3' }}>Илгээгч</div>
            <div className="text-[14px] text-white break-all">{giftCard.senderEmail}</div>
          </div>
        )}
      </div>
      {giftCard?.message && (
        <div
          className="mt-5 rounded-lg p-4 text-[13px] leading-6 italic"
          style={{
            background: 'rgba(0,0,0,0.25)',
            border: '1px solid rgba(255,255,255,0.07)',
            color: 'rgba(255,255,255,0.7)',
            fontFamily: 'Gilroy, Inter, sans-serif',
          }}
        >
          &ldquo;{giftCard.message}&rdquo;
        </div>
      )}
    </div>
  )
}

function GiftPaymentCard({ giftCard }: { giftCard: any }) {
  const paid = !!giftCard?.paidAt || ['paid', 'sent', 'used'].includes(giftCard?.status)
  return (
    <div className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-4" style={{ color: '#a8a8b3' }}>Төлбөр</div>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[14px] text-white font-medium">QPay</div>
          {giftCard?.paidAt && (
            <div className="text-[12px] mt-1 tabular-nums" style={{ color: 'rgba(255,255,255,0.55)' }}>
              {formatDateTimeNormal(giftCard.paidAt)}
            </div>
          )}
        </div>
        <div
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] shrink-0"
          style={{ color: paid ? '#34d399' : '#fbbf24' }}
        >
          <span className="inline-flex w-1.5 h-1.5 rounded-full" style={{ background: paid ? '#34d399' : '#fbbf24' }} />
          {paid ? 'Төлсөн' : 'Төлбөл зохих'}
        </div>
      </div>
    </div>
  )
}

function GiftTimeline({ giftCard }: { giftCard: any }) {
  const status = giftCard?.status
  const STEPS = [
    { key: 'pending', label: 'Захиалсан', at: giftCard?.createdAt },
    { key: 'paid', label: 'Төлбөр баталгаажсан', at: giftCard?.paidAt },
    { key: 'sent', label: 'Илгээгдсэн', at: giftCard?.sentAt },
    { key: 'used', label: 'Ашиглагдсан', at: giftCard?.usedAt },
  ]
  const ORDER = ['pending', 'paid', 'sent', 'used']
  const currentIdx = Math.max(0, ORDER.indexOf(status))

  return (
    <div className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
      <div className="hidden sm:block">
        <div className="flex items-center">
          {STEPS.map((s, i) => {
            const done = i < currentIdx
            const active = i === currentIdx
            return (
              <React.Fragment key={s.key}>
                <span
                  className="w-7 h-7 rounded-full inline-flex items-center justify-center text-[10px] font-semibold tabular-nums shrink-0"
                  style={{
                    background: done ? '#34d399' : active ? 'white' : 'rgba(255,255,255,0.035)',
                    color: done ? '#063' : active ? '#0a0a0c' : 'rgba(255,255,255,0.55)',
                    border: done ? '1px solid #34d399' : active ? '1px solid white' : '1px solid rgba(255,255,255,0.07)',
                  }}
                >
                  {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : String(i + 1).padStart(2, '0')}
                </span>
                {i < STEPS.length - 1 && (
                  <div className="flex-1 h-px mx-2" style={{ background: done ? '#34d399' : 'rgba(255,255,255,0.07)' }} />
                )}
              </React.Fragment>
            )
          })}
        </div>
        <div className="mt-3 grid" style={{ gridTemplateColumns: `repeat(${STEPS.length}, minmax(0, 1fr))` }}>
          {STEPS.map((s, i) => {
            const done = i < currentIdx
            const active = i === currentIdx
            return (
              <div
                key={s.key}
                className="min-w-0"
                style={{ textAlign: i === 0 ? 'left' : i === STEPS.length - 1 ? 'right' : 'center' }}
              >
                <div
                  className="text-[12px] font-medium leading-tight"
                  style={{ color: active || done ? 'white' : 'rgba(255,255,255,0.55)' }}
                >
                  {s.label}
                </div>
                {s.at && (
                  <div className="mt-1 text-[11px] tabular-nums" style={{ color: 'rgba(255,255,255,0.4)' }}>
                    {formatDateTimeNormal(s.at)}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      <ol className="sm:hidden space-y-4">
        {STEPS.map((s, i) => {
          const done = i < currentIdx
          const active = i === currentIdx
          return (
            <li key={s.key} className="flex gap-3 items-start">
              <div className="flex flex-col items-center shrink-0">
                <span
                  className="w-7 h-7 rounded-full inline-flex items-center justify-center text-[10px] font-semibold tabular-nums"
                  style={{
                    background: done ? '#34d399' : active ? 'white' : 'rgba(255,255,255,0.035)',
                    color: done ? '#063' : active ? '#0a0a0c' : 'rgba(255,255,255,0.55)',
                    border: done ? '1px solid #34d399' : active ? '1px solid white' : '1px solid rgba(255,255,255,0.07)',
                  }}
                >
                  {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : String(i + 1).padStart(2, '0')}
                </span>
                {i < STEPS.length - 1 && (
                  <span className="w-px flex-1 mt-1 min-h-6" style={{ background: done ? '#34d399' : 'rgba(255,255,255,0.07)' }} />
                )}
              </div>
              <div className="flex-1 min-w-0 pb-1">
                <div
                  className="text-[13px] font-medium leading-tight"
                  style={{ color: active || done ? 'white' : 'rgba(255,255,255,0.55)' }}
                >
                  {s.label}
                </div>
                {s.at && (
                  <div className="mt-0.5 text-[11px] tabular-nums" style={{ color: 'rgba(255,255,255,0.4)' }}>
                    {formatDateTimeNormal(s.at)}
                  </div>
                )}
              </div>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

function PrintableGiftReceipt({ giftCard, vendor }: { giftCard: any, vendor: any }) {
  const [mounted, setMounted] = useState(false)
  useEffect(() => { setMounted(true) }, [])
  if (!mounted) return null
  const recipient = giftCard?.fullName || `${giftCard?.firstName || ''} ${giftCard?.lastName || ''}`.trim()
  const label = STATUS_LABELS[giftCard?.status] || '—'
  return createPortal(
    <div data-print-root className="hidden print:block text-black text-[12px] leading-snug">
      <style>{'@page { size: A4; margin: 16mm; }'}</style>
      <div className="text-center mb-4">
        <div className="text-lg font-bold">{vendor?.name || 'Shop.mn'}</div>
        <div className="text-[11px]">Бэлгийн картын баримт</div>
      </div>
      <div className="flex justify-between border-y border-black py-2 mb-3">
        <div>
          <div className="font-bold">Карт #{giftCard?.customId}</div>
          {giftCard?.createdAt && <div className="text-[11px]">{formatDateTimeLong(giftCard.createdAt)}</div>}
        </div>
        <div className="text-right">
          <div className="text-[11px]">Төлөв</div>
          <div className="font-bold">{label}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-3">
        <div>
          <div className="text-[10px] uppercase font-bold mb-1">Хүлээн авагч</div>
          <div>{recipient || '—'}</div>
          {giftCard?.email && <div>{giftCard.email}</div>}
        </div>
        <div>
          <div className="text-[10px] uppercase font-bold mb-1">Илгээгч</div>
          <div>{giftCard?.senderEmail || '—'}</div>
          {giftCard?.deliveryDate && <div>Илгээх: {formatDateTimeLong(giftCard.deliveryDate)}</div>}
        </div>
      </div>
      {giftCard?.message && (
        <div className="mb-3">
          <div className="text-[10px] uppercase font-bold mb-1">Зурвас</div>
          <div className="italic">&ldquo;{giftCard.message}&rdquo;</div>
        </div>
      )}
      <table className="w-full border-collapse mb-3">
        <thead>
          <tr className="border-y border-black">
            <th className="text-left py-1 font-bold">Зүйл</th>
            <th className="text-right py-1 font-bold w-32">Дүн</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-zinc-300 align-top">
            <td className="py-1 pr-2">Бэлгийн карт{giftCard?.template?.name ? ` · ${giftCard.template.name}` : ''}</td>
            <td className="py-1 text-right tabular-nums">{formatPrice(giftCard?.amount)}</td>
          </tr>
        </tbody>
      </table>
      <div className="ml-auto w-1/2 text-[11px]">
        <div className="flex justify-between border-t border-black mt-1 pt-1 font-bold text-[13px]">
          <span>Нийт</span>
          <span className="tabular-nums">{formatPrice(giftCard?.amount)}</span>
        </div>
      </div>
      {giftCard?.code && (
        <div className="mt-4 border border-black p-3">
          <div className="text-[10px] uppercase font-bold mb-1">Картын код</div>
          <div className="text-[16px] font-mono tracking-wider">{giftCard.code}</div>
        </div>
      )}
      <div className="mt-6 text-center text-[10px]">
        Энэхүү баримтыг {formatDateTimeLong(new Date())}-нд хэвлэв · {vendor?.name || 'Shop.mn'}
      </div>
    </div>,
    document.body,
  )
}

export default function GiftDetail({ giftCard, vendor }: { giftCard: any, vendor?: any }) {
  const router = useRouter()
  const pathname = usePathname() || ''
  const status = giftCard?.status
  const tone = TONES[STATUS_TONE_MAP[status] || 'muted']
  const recipient = giftCard?.fullName || `${giftCard?.firstName || ''} ${giftCard?.lastName || ''}`.trim()

  const showPending = status === 'pending'
  const showPaidBanner = ['paid', 'sent', 'used'].includes(status)
  const showCanceled = status === 'canceled'

  return (
    <div>
      <div className="print:hidden">
        <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pt-10 pb-6" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="hidden md:flex items-center gap-1.5 text-[12px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
            <a href={navigateUrl('/', pathname)} className="hover:text-white transition-colors">Нүүр</a>
            <ChevronRight className="h-3 w-3" style={{ opacity: 0.5 }} />
            <a href={navigateUrl('/gift', pathname)} className="hover:text-white transition-colors">Бэлгийн карт</a>
            <ChevronRight className="h-3 w-3" style={{ opacity: 0.5 }} />
            <span className="text-white font-medium">#{giftCard?.customId}</span>
          </div>
          <div className="mt-4 flex items-end justify-between gap-6 flex-wrap">
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>Бэлгийн карт</div>
              <h1
                className="mt-2 font-semibold tracking-[-0.025em] leading-[0.95] text-white"
                style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(28px, 5vw, 44px)' }}
              >
                #{giftCard?.customId}
              </h1>
              <div className="mt-2 text-[13px] tabular-nums" style={{ color: 'rgba(255,255,255,0.55)' }}>
                {giftCard?.createdAt ? formatDateTimeNormal(giftCard.createdAt) : '—'}
                {recipient ? ` · ${recipient}` : ''}
              </div>
            </div>
            <div
              className="inline-flex items-center gap-2 h-9 px-4 rounded-full text-[12px] font-semibold uppercase tracking-[0.16em]"
              style={{ background: tone.bg, color: tone.color, border: `1px solid ${tone.border}` }}
            >
              <span className="inline-flex w-1.5 h-1.5 rounded-full" style={{ background: tone.color }} />
              {STATUS_LABELS[status] || 'Тодорхойгүй'}
            </div>
          </div>
        </div>

        <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pt-8 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8">
            <div className="space-y-5 min-w-0">
              {showPending && <PendingPayment giftCard={giftCard} onSuccess={() => router.refresh()} />}
              {showPaidBanner && <PaidBanner giftCard={giftCard} status={status} />}
              {showCanceled && <CanceledBanner giftCard={giftCard} />}

              <GiftTimeline giftCard={giftCard} />
              <GiftCardSection giftCard={giftCard} vendor={vendor} />
              <GiftRecipientCard giftCard={giftCard} />
              <GiftPaymentCard giftCard={giftCard} />
            </div>

            <aside className="lg:sticky lg:top-32 self-start space-y-4">
              <div className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-4" style={{ color: '#a8a8b3' }}>Хураангуй</div>
                <div className="space-y-2.5 text-[13px]">
                  <div className="flex justify-between gap-3">
                    <span style={{ color: 'rgba(255,255,255,0.6)' }}>Картын дүн</span>
                    <span className="tabular-nums text-white">{formatPrice(giftCard?.amount)}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span style={{ color: 'rgba(255,255,255,0.6)' }}>Үйлчилгээний хураамж</span>
                    <span className="tabular-nums text-white">Үнэгүй</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 flex items-baseline justify-between" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                  <span className="text-[13px] text-white">Нийт</span>
                  <span className="text-[22px] font-semibold tabular-nums text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>
                    {formatPrice(giftCard?.amount)}
                  </span>
                </div>
              </div>

              {giftCard?.deliveryDate && (
                <div className="rounded-2xl p-5 flex items-center gap-3" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <div className="w-9 h-9 rounded-full inline-flex items-center justify-center shrink-0" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.07)', color: 'white' }}>
                    <Clock className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: '#a8a8b3' }}>Илгээгдэх огноо</div>
                    <div className="mt-0.5 text-[13.5px] text-white tabular-nums">{formatDateTimeNormal(giftCard.deliveryDate)}</div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                {giftCard?.code && (
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard?.writeText(giftCard.code)
                      toast.success('Код хуулагдлаа')
                    }}
                    className="w-full h-12 rounded-full text-[13.5px] font-medium inline-flex items-center justify-center gap-2 transition-opacity cursor-pointer"
                    style={{ background: 'white', color: '#0a0a0c' }}
                  >
                    <Copy className="h-3.5 w-3.5" /> Кодыг хуулах
                  </button>
                )}
                <a
                  href={navigateUrl('/gift', pathname)}
                  className="w-full h-12 rounded-full text-[13.5px] font-medium inline-flex items-center justify-center gap-2 transition-colors hover:bg-white/10"
                  style={{
                    background: giftCard?.code ? 'rgba(255,255,255,0.035)' : 'white',
                    color: giftCard?.code ? 'white' : '#0a0a0c',
                    border: giftCard?.code ? '1px solid rgba(255,255,255,0.07)' : 'none',
                  }}
                >
                  Шинэ карт авах <ArrowRight className="h-3.5 w-3.5" />
                </a>
                {(vendor?.email || vendor?.phone) && (
                  <a
                    href={vendor?.email ? `mailto:${vendor.email}` : `tel:${vendor.phone}`}
                    className="w-full h-12 rounded-full text-[13.5px] font-medium inline-flex items-center justify-center gap-2 transition-colors hover:bg-white/5"
                    style={{ background: 'transparent', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.07)' }}
                  >
                    <MessageCircle className="h-3.5 w-3.5" /> Дэмжлэг авах
                  </a>
                )}
              </div>

              {(vendor?.email || vendor?.phone) && (
                <div className="text-[11px] text-center" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  Тусламж хэрэгтэй бол{' '}
                  {vendor?.email && <a href={`mailto:${vendor.email}`} className="text-white underline underline-offset-2">{vendor.email}</a>}
                  {vendor?.email && vendor?.phone && ' эсвэл '}
                  {vendor?.phone && <a href={`tel:${vendor.phone}`} className="text-white">{vendor.phone}</a>}
                  {' '}руу хандаарай.
                </div>
              )}

              <button
                type="button"
                onClick={() => window.print()}
                className="w-full inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold cursor-pointer transition-colors hover:text-white hover:bg-white/5 rounded-full py-2 -my-2"
                style={{ color: 'rgba(255,255,255,0.55)' }}
              >
                <Printer className="h-3 w-3" /> Баримт хэвлэх
              </button>
            </aside>
          </div>
        </div>
      </div>

      <PrintableGiftReceipt giftCard={giftCard} vendor={vendor} />
    </div>
  )
}
